import React, { useState, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Card from "../../../components/Card";
import { API_CREDITS_GET } from "../../../service/url";
import { getApiCall } from "../../../service/httpCall";
import { DataTable } from "primereact/datatable";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import { Column } from "primereact/column";
import ToastNotification from "../../../components/Toast";
import { errorCheck } from "../auth/auth-utils";
import exportData from "../../../utilities/export";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import "../../../assets/custom/css/custom.css";
import {
  FaArrowUp,
  FaArrowDown,
  FaExchangeAlt,
  FaMicrophone,
} from "react-icons/fa";

import { fetchAndSetFlashMessage } from "../../../service/fetchFlashMessage";
import {
  FLASH_MESSAGE_ACTION_OBJECT,
  MODULE_ACL,
  USER_ROLES,
} from "../../../utilities/constant";
import AsyncStorage from "@react-native-async-storage/async-storage";

import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import Loader from "../../../components/partials/components/loader";

const CreditHistory = () => {
  const navigate = useNavigate();

  const [creditsList, setCreditsList] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCredits, setFilteredCredits] = useState([]);

  const [filters] = useState({
    name: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
  });

  const [loading, setLoading] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);
  const [loggedInUserAssignedCompanyId, setLoggedInUserAssignedCompanyId] =
    useState(null);
  const [loggedInUserRole, setLoggedInUserRole] = useState(null);
  const [loggedInUserAssignedCompanyName, setLoggedInUserAssignedCompanyName] =
    useState(null);

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const fetchCredits = async (start, end) => {
    try {
      setLoading(true);

      const url = `${API_CREDITS_GET}?startDate=${formatDate(start)}&endDate=${formatDate(end)}`;

      const resp = await getApiCall(url, {});
      setCreditsList(resp);
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

    setStartDate(firstDay);
    setEndDate(today);

    fetchCredits(firstDay, today);

    fetchAndSetFlashMessage(
      (msg) =>
        msg.module_name === MODULE_ACL.CREDIT_HISTORY &&
        msg.action === FLASH_MESSAGE_ACTION_OBJECT.LIST &&
        msg.type === "Flash Message",
      setAlertMessage,
    );
  }, []);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredCredits(creditsList);
      return;
    }

    const lower = searchTerm.toLowerCase();

    const filtered = creditsList.filter((item) => {
      const fromName = item.from?.name?.toLowerCase() || "";
      const toName = item.to?.name?.toLowerCase() || "";
      const actionOnName = item.actionOn?.name?.toLowerCase() || "";
      const action = item.action?.toLowerCase() || "";

      return (
        fromName.includes(lower) ||
        toName.includes(lower) ||
        actionOnName.includes(lower) ||
        action.includes(lower)
      );
    });

    setFilteredCredits(filtered);
  }, [searchTerm, creditsList]);

  // 🔹 FILTER CLICK
  const handleFilter = () => {
    if (!startDate || !endDate) {
      setToastProps({ message: "Please select both dates", type: "error" });
      setShowToast(true);
      return;
    }

    const today = new Date();

    // ❌ future date validation
    if (endDate > today) {
      setToastProps({ message: "End date cannot be in future", type: "error" });
      setShowToast(true);
      return;
    }

    if (startDate > endDate) {
      setToastProps({
        message: "Start date cannot be after end date",
        type: "error",
      });
      setShowToast(true);
      return;
    }

    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    if (startDate < sixMonthsAgo) {
      setToastProps({
        message: "Maximum 6 months range allowed",
        type: "error",
      });
      setShowToast(true);
      return;
    }

    fetchCredits(startDate, endDate);
  };

  const handleReset = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    setSearchTerm("");

    setStartDate(firstDay);
    setEndDate(today);

    fetchCredits(firstDay, today);
  };

  const handleExport = async () => {
    setButtonDisable(true);
    await exportData("Credits", setToastProps, setShowToast);
    setButtonDisable(false);
  };

  useEffect(() => {
    if (toastProps) setShowToast(true);
  }, [toastProps]);

  function onError(err) {
    if (typeof err !== "object" && err.includes("TokenExpiredError")) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  useEffect(() => {
    async function superadminCheck() {
      const rawUser = await AsyncStorage.getItem("user");
      if (!rawUser) {
        console.error("User not found in AsyncStorage");
        return null; // or handle as needed
      }

      let user;
      try {
        user = JSON.parse(rawUser);
      } catch (e) {
        console.error("Invalid JSON in user from AsyncStorage", e);
        return null;
      }
      if (user.role) {
        setLoggedInUserRole(user.role);
      }
      if (user.role !== USER_ROLES.SUPERADMIN) {
        setLoggedInUserAssignedCompanyName(user?.assignedCompany?.name);
        setLoggedInUserAssignedCompanyId(user?.assignedCompany?.id);
      }
    }

    superadminCheck();
  }, []);

  const getSummary = () => {
    if (!creditsList || creditsList.length === 0) return null;

    let summary = {
      credit: 0,
      debit: 0,
      inbound_deduction: 0,
      outbound_deduction: 0,
      tts_deduction: 0,
      tts_addition: 0,
    };

    creditsList.forEach((item) => {
      if (
        item.action_id == loggedInUserAssignedCompanyId &&
        item.action_type === "Company"
      ) {
        const amount = Number(item.credits_rupees || 0);

        switch (item.action) {
          case "addition":
            summary.credit += amount;
            break;
          case "deduction":
            summary.debit += amount;
            break;
          case "inbound_deduction":
            summary.inbound_deduction += amount;
            break;
          case "outbound_deduction":
            summary.outbound_deduction += amount;
            break;
          case "tts_deduction":
            summary.tts_deduction += amount;
            break;
          case "tts_addition":
            summary.tts_addition += amount;
            break;
          default:
            break;
        }
      }
    });

    return summary;
  };

  const summary = getSummary();

  if (loading) return <Loader />;

  return (
    <>
      {alertMessage && (
        <div
          className="alert alert-success marquee-container"
          style={{ marginTop: "5px" }}
        >
          <span className="marquee-text">{alertMessage}</span>
        </div>
      )}

      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between">
              <h4 className="card-title">Credit History</h4>

              {creditsList.length > 0 && (
                <Button
                  label="Export Data"
                  icon="pi pi-file-export"
                  onClick={handleExport}
                  className="p-button-active p-button-sm"
                  disabled={buttonDisable}
                />
              )}
            </Card.Header>

            <Card.Body className="px-0">
              <div
                className="d-flex align-items-center mb-3"
                style={{ marginLeft: "10px" }}
              >
                <Calendar
                  value={startDate}
                  onChange={(e) => setStartDate(e.value)}
                  showIcon
                />
                <span className="mx-2">to</span>
                <Calendar
                  value={endDate}
                  onChange={(e) => setEndDate(e.value)}
                  showIcon
                />

                <Button
                  label="Filter"
                  onClick={handleFilter}
                  className="mx-2"
                />
                <Button
                  label="Reset"
                  onClick={handleReset}
                  className="p-button-secondary"
                />
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  margin: "15px",
                }}
              >
                <div
                  style={{
                    position: "relative",
                    width: "320px",
                  }}
                >
                  {/* INPUT */}
                  <input
                    type="text"
                    placeholder="Search by name or action..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px 15px 10px 15px",
                      borderRadius: "12px",
                      border: "1px solid rgba(132, 132, 132, 0.7)",
                      background: "rgba(255,255,255,0.05)",
                      fontSize: "14px",
                      outline: "none",
                      backdropFilter: "blur(6px)",
                      transition: "all 0.25s ease",
                    }}
                    onFocus={(e) => {
                      e.target.style.border = "1px solid #4f8cff";
                      e.target.style.boxShadow =
                        "0 0 0 2px rgba(79,140,255,0.2)";
                      e.target.style.background = "rgba(255,255,255,0.08)";
                    }}
                    onBlur={(e) => {
                      e.target.style.border = "1px solid rgba(255,255,255,0.1)";
                      e.target.style.boxShadow = "none";
                      e.target.style.background = "rgba(255,255,255,0.05)";
                    }}
                  />
                </div>
              </div>

              {loggedInUserRole !== USER_ROLES.SUPERADMIN && summary && (
                <div
                  style={{
                    padding: "0 15px",
                    marginBottom: "50px",
                    marginTop: "50px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "15px",
                    }}
                  >
                    <h5 style={{ margin: 0, fontWeight: "600" }}>
                      Summary ({loggedInUserAssignedCompanyName})
                    </h5>

                    {startDate && endDate && (
                      <small style={{ color: "#777" }}>
                        {startDate.toLocaleDateString()} -{" "}
                        {endDate.toLocaleDateString()}
                      </small>
                    )}
                  </div>
                  <p style={{ marginBottom: "30px", fontWeight: "600", fontStyle: "italic", opacity: 0.7, fontSize: "13px", marginTop: "-10px" }}>
                    Assigned Company
                  </p>

                  <div className="row g-3">
                    {[
                      {
                        label: "Credit",
                        value: summary.credit,
                        icon: <FaArrowUp />,
                        color: "#28a745",
                      },
                      {
                        label: "Debit",
                        value: summary.debit,
                        icon: <FaArrowDown />,
                        color: "#dc3545",
                      },
                      {
                        label: "Inbound Deduction",
                        value: summary.inbound_deduction,
                        icon: <FaExchangeAlt />,
                        color: "#007bff",
                      },
                      {
                        label: "Outbound Deduction",
                        value: summary.outbound_deduction,
                        icon: <FaExchangeAlt />,
                        color: "#6f42c1",
                      },
                      {
                        label: "TTS Deduction",
                        value: summary.tts_deduction,
                        icon: <FaMicrophone />,
                        color: "#fd7e14",
                      },
                      {
                        label: "TTS Addition",
                        value: summary.tts_addition,
                        icon: <FaMicrophone />,
                        color: "#20c997",
                      },
                    ].map((item, index) => (
                      <div className="col-md-4" key={index}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "15px",
                            padding: "18px",
                            borderRadius: "14px",
                            background: "#fff",
                            boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                            borderLeft: `5px solid ${item.color}`,
                            transition: "all 0.25s ease",
                            cursor: "pointer",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform =
                              "translateY(-4px)";
                            e.currentTarget.style.boxShadow =
                              "0 10px 25px rgba(0,0,0,0.15)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0px)";
                            e.currentTarget.style.boxShadow =
                              "0 6px 18px rgba(0,0,0,0.08)";
                          }}
                        >
                          <div style={{ fontSize: "22px", color: item.color }}>
                            {item.icon}
                          </div>

                          <div>
                            <p
                              style={{
                                margin: 0,
                                fontSize: "13px",
                                color: "#777",
                              }}
                            >
                              {item.label}
                            </p>

                            <h5
                              style={{
                                margin: 0,
                                fontWeight: "600",
                              }}
                            >
                              ₹{item.value}
                            </h5>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ width: "100%", overflowX: "scroll" }}>
                {/* TABLE */}
                <DataTable
                  value={filteredCredits}
                  paginator
                  rows={8}
                  filters={filters}
                >
                  <Column
                    header="#"
                    body={(rowData, { rowIndex }) => rowIndex + 1}
                  />

                  <Column
                    header="From"
                    body={(rowData) =>
                      rowData.from
                        ? `${rowData.from.name} (${rowData.from.type})`
                        : "-"
                    }
                  />

                  <Column
                    header="To"
                    body={(rowData) =>
                      rowData.to
                        ? `${rowData.to.name} (${rowData.to.type})`
                        : "-"
                    }
                  />

                  <Column
                    header="Action On"
                    body={(rowData) =>
                      rowData.actionOn
                        ? `${rowData.actionOn.name} (${rowData.actionOn.type})`
                        : "-"
                    }
                  />

                  <Column
                    field="req_date"
                    header="Date"
                    body={(rowData) =>
                      new Date(rowData.req_date).toLocaleString("en-IN")
                    }
                  />

                  <Column
                    header="Action"
                    body={(rowData) => {
                      const action = rowData.action;

                      const isAddition =
                        action === "addition" || action === "tts_addition";

                      const isDeduction =
                        action === "deduction" ||
                        action === "inbound_deduction" ||
                        action === "outbound_deduction" ||
                        action === "tts_deduction";

                      return (
                        <span style={{ display: "flex", alignItems: "center", gap: "6px", color: isAddition ? "green" : "red" }}>
                          {isAddition && <FaArrowUp style={{ color: "green" }} />}
                          {isDeduction && <FaArrowDown style={{ color: "red" }} />}
                          {action}
                        </span>
                      );
                    }}
                  />
                  <Column field="credits_rupees" header="Amount" />
                  <Column field="balance" header="Balance" />
                </DataTable>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <ToastNotification
        show_toast={showToast}
        set_show_toast={setShowToast}
        {...toastProps}
      />
    </>
  );
};

export default CreditHistory;
